/**
 * Blackwaxx&Co Feature hero slideshow (design-system/project/components/HeroFeature/README.md).
 * Slide timer with a 600ms crossfade (CSS), previous / next, progress-bar dots, pause / play. Autoplay is off under
 * prefers-reduced-motion, pauses while focus is inside the hero and while the tab is hidden.
 */
class BwFeature extends HTMLElement {
  #index = 0;
  #timer = null;
  #paused = false;
  #focusPaused = false;
  #userPaused = false;

  connectedCallback() {
    this.images = [...this.querySelectorAll('.bw-feature__slide')];
    this.texts = [...this.querySelectorAll('[data-slide-text]')];
    this.cards = [...this.querySelectorAll('[data-slide-card]')];
    this.dots = [...this.querySelectorAll('[data-dot]')];
    this.pauseButton = this.querySelector('[data-pause]');
    this.count = this.texts.length;
    this.seconds = Math.max(6, Number(this.dataset.seconds) || 6);

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#userPaused = this.dataset.autoplay !== 'true' || reduced;
    this.#syncPause();

    this.querySelector('[data-prev]')?.addEventListener('click', () => this.show(this.#index - 1, true));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.show(this.#index + 1, true));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.show(i, true)));
    this.pauseButton?.addEventListener('click', () => {
      this.#userPaused = !this.#userPaused;
      this.#syncPause();
    });

    this.addEventListener('focusin', () => {
      this.#focusPaused = true;
      this.#schedule();
    });
    this.addEventListener('focusout', (event) => {
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;
      this.#focusPaused = false;
      this.#schedule();
    });
    document.addEventListener('visibilitychange', this.#onVisibility);

    // Theme editor: jump to the slide being edited
    this.addEventListener('shopify:block:select', (event) => {
      const block = event.target.closest?.('picture');
      const i = block ? [...this.querySelectorAll('picture')].indexOf(block) : -1;
      if (i > -1) this.show(i, false);
    });

    this.#render();
    this.#schedule();
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    document.removeEventListener('visibilitychange', this.#onVisibility);
  }

  #onVisibility = () => this.#schedule();

  show(index, restart) {
    this.#index = (index + this.count) % this.count;
    this.#render();
    if (restart) this.#schedule();
  }

  #syncPause() {
    this.classList.toggle('is-paused', this.#userPaused);
    if (this.pauseButton) {
      this.pauseButton.setAttribute('aria-label', this.#userPaused ? 'Play slideshow' : 'Pause slideshow');
      this.pauseButton.querySelector('[data-icon-pause]').hidden = this.#userPaused;
      this.pauseButton.querySelector('[data-icon-play]').hidden = !this.#userPaused;
    }
    // Restart the progress animation from the current dot
    this.#render();
    this.#schedule();
  }

  #render() {
    this.images.forEach((img, i) => img.classList.toggle('is-active', i === this.#index));
    this.texts.forEach((el, i) => {
      el.hidden = i !== this.#index;
    });
    this.cards.forEach((el, i) => {
      el.hidden = i !== this.#index;
    });
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-done', i < this.#index);
      dot.classList.toggle('is-active', i === this.#index);
      if (i === this.#index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  #schedule() {
    clearTimeout(this.#timer);
    if (this.count < 2 || this.#userPaused || this.#focusPaused || document.hidden) return;
    this.#timer = setTimeout(() => this.show(this.#index + 1, true), this.seconds * 1000);
  }
}

if (!customElements.get('bw-feature')) customElements.define('bw-feature', BwFeature);
